import React, { useCallback, useEffect, useRef, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";

import LargeLogo from "../LargeLogo/LargeLogo";
import { useDispatch } from "react-redux";
import {
    hasAccess,
    httpRequest,
    isDev,
    printLog,
    userLogout,
} from "../../helper/global";
import { useNavigate } from "react-router-dom";
import { Link } from "@mui/material";
import { useTheme } from "../../contexts/ThemeContext";
import { PERMISSIONS } from "../../helper/constant";

function HomeAppBar({ ref, selectedPage = "", user, permissionConfig }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const defaultRef = useRef();

    const { theme, mode, toggleTheme } = useTheme();

    const [anchorElNav, setAnchorElNav] = useState(null);
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [pages, setPages] = useState([]);

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const getUserName = useCallback(() => {
        if (!user) {
            return "";
        }

        return `${user.firstName} ${user.lastName}`;
    }, [user]);

    const handleLogout = async () => {
        const result = await httpRequest("user/logout", {}, "POST");

        userLogout(dispatch, navigate);
    };

    useEffect(() => {
        if (!user || !permissionConfig) {
            return;
        }

        const pages = [
            // {
            //     label: "Dashboard",
            //     callback: () => {
            //         navigate("/home");
            //     },
            // },
        ];

        if (hasAccess(user, permissionConfig, "patient", PERMISSIONS.read)) {
            pages.push({
                label: "Patients",
                callback: () => {
                    navigate("/patients");
                },
            });
        }

        if (hasAccess(user, permissionConfig, "user", PERMISSIONS.read)) {
            pages.push({
                label: "Users",
                callback: () => {
                    navigate("/users");
                },
            });
        }

        if (hasAccess(user, permissionConfig, "email", PERMISSIONS.write)) {
            pages.push({
                label: "Emails",
                callback: () => {
                    navigate("/emails");
                },
            });
        }

        if (hasAccess(user, permissionConfig, "education", PERMISSIONS.write)) {
            pages.push({
                label: "Education",
                callback: () => {
                    navigate("/education");
                },
            });
        }

        setPages(pages);
    }, [user, permissionConfig]);

    const settings = [
        // { label: "Account", callback: null },
        { label: "Logout", callback: handleLogout },
    ];

    return (
        <AppBar
            ref={ref ?? defaultRef}
            position="fixed"
            sx={{ backgroundColor: theme.palette.background.mainPalette }}
        >
            <Container sx={theme.homeAppBar}>
                <Toolbar disableGutters>
                    <Box
                        sx={{
                            width: "120px",
                            display: { xs: "none", md: "flex" },
                            mr: 5,
                        }}
                    >
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => {
                                navigate("/");
                            }}
                        >
                            <LargeLogo />
                        </Link>
                    </Box>

                    <Box
                        sx={{
                            flexGrow: 1,
                            display: { xs: "flex", md: "none" },
                        }}
                    >
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{ display: { xs: "block", md: "none" } }}
                        >
                            {pages.map((page, index) => (
                                <MenuItem
                                    key={`page-${index}`}
                                    onClick={page.callback}
                                    sx={{
                                        bgcolor:
                                            page.label.toLowerCase() ===
                                            selectedPage.toLowerCase()
                                                ? theme.palette.backgroundColor
                                                      .navbarSelected
                                                : theme.palette.backgroundColor
                                                      .transparent,
                                    }}
                                >
                                    <Typography sx={{ textAlign: "center" }}>
                                        {page.label}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    <Box
                        sx={{
                            width: "120px",
                            display: { xs: "flex", md: "none" },
                            position: "absolute",
                            left: "50%",
                            "-ms-transform": "translateX(-50%) !important",
                            transform: "translateX(-50%) !important",
                        }}
                    >
                        <LargeLogo />
                    </Box>

                    <Box
                        sx={{
                            flexGrow: 1,
                            display: { xs: "none", md: "flex" },
                        }}
                    >
                        {pages.map((page, index) => (
                            <Button
                                key={`page-${index}`}
                                onClick={page.callback}
                                sx={{
                                    my: 2,
                                    color: "white",
                                    display: "block",
                                    bgcolor:
                                        page.label.toLowerCase() ===
                                        selectedPage.toLowerCase()
                                            ? theme.palette.backgroundColor
                                                  .navbarSelected
                                            : theme.palette.backgroundColor
                                                  .transparent,

                                    color:
                                        page.label.toLowerCase() ===
                                        selectedPage.toLowerCase()
                                            ? theme.palette.color.main
                                            : "white",
                                }}
                            >
                                {page.label}
                            </Button>
                        ))}
                    </Box>
                    {isDev() && (
                        <Box sx={{ backgroundColor: "red", px: 1, mx: 2 }}>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: "bold" }}
                            >
                                DEVELOPMENT VERSION
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ flexGrow: 0 }}>
                        <Tooltip title="Open settings">
                            <IconButton
                                onClick={handleOpenUserMenu}
                                sx={{ p: 0 }}
                            >
                                <Avatar src={user ? `${user.avatar}` : "A"} />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: "45px" }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            <MenuItem key="user-name" disabled>
                                <Typography sx={{ textAlign: "center" }}>
                                    {getUserName()}
                                </Typography>
                            </MenuItem>

                            {settings.map((setting, index) => (
                                <MenuItem
                                    key={`setting-${index}`}
                                    onClick={() => {
                                        handleCloseUserMenu();

                                        if (!setting.callback) {
                                            return;
                                        }

                                        setting.callback();
                                    }}
                                >
                                    <Typography sx={{ textAlign: "center" }}>
                                        {setting.label}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                    <IconButton
                        sx={{ ml: 1 }}
                        onClick={toggleTheme}
                        color="inherit"
                    >
                        {mode === "dark" ? (
                            <Brightness7Icon />
                        ) : (
                            <Brightness4Icon />
                        )}
                    </IconButton>
                </Toolbar>
            </Container>
        </AppBar>
    );
}
export default HomeAppBar;
